import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { useSettings, resolveImageUrl } from "../../lib/SettingsContext";

// Detect if URL is a YouTube link; convert to embed URL
function toEmbed(url) {
  if (!url) return null;
  // youtube watch
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=0&modestbranding=1&rel=0`;
  // vimeo
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
}

export default function VideoShowcase() {
  const { settings } = useSettings();
  const raw = settings?.showcase_video_url || "";
  if (!raw) return null;

  const embed = toEmbed(raw);
  const directVideo = !embed && (raw.match(/\.(mp4|webm|ogg)(\?|$)/i) || raw.startsWith("/api/"));
  const resolved = resolveImageUrl(raw);
  const title = settings?.showcase_video_title || "Watch our work in motion";

  return (
    <section id="video-showcase" className="py-24 bg-brand-mist">
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center"
        >
          <div className="overline text-brand-green">Our Showreel</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-brand-ink leading-tight tracking-tight">
            {title}
          </h2>
          <p className="mt-4 text-gray-600 leading-relaxed">
            A glimpse of the brand films, reels and product videos we craft for our clients.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 max-w-5xl mx-auto rounded-3xl overflow-hidden border border-gray-200 shadow-2xl bg-black"
        >
          <div className="relative aspect-video w-full">
            {embed ? (
              <iframe
                src={embed}
                title={title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            ) : directVideo ? (
              <video
                src={resolved}
                controls
                playsInline
                className="absolute inset-0 w-full h-full object-cover bg-black"
              >
                Your browser does not support video.
              </video>
            ) : (
              <div className="absolute inset-0 grid place-items-center text-white/70">
                <div className="text-center">
                  <Play className="h-12 w-12 mx-auto mb-3 opacity-60" />
                  <p>Video URL not supported. Use a YouTube, Vimeo, or direct MP4 link.</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
